import $ from 'jquery'
import { Util } from 'bootstrap'

import Confirm from './confirm'

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'nav'
const DATA_KEY = `opp.${NAME}`
const EVENT_KEY = '.' + DATA_KEY
const DATA_API_KEY = '.data-api'
const JQUERY_NO_CONFLICT = $.fn[NAME]

const Default = {
  confirm: '',
  enable: true
}

const DefaultType = {
  confirm: '(string|element)',
  enable: 'boolean'
}

const Selector = {
  DATA_TOGGLE_NAV: '[data-toggle="nav"]',
  DATA_NAV_CONFIRM: '[data-nav="confirm"]',
  DATA_NAV_IGNORE: '[data-nav="ignore"]',
  DATA_NAV_TRIGGER: 'a[href]:not([href^="#"]):not([target="_blank"])'
}

const Event = {
  NAVIGATE: `navigate${EVENT_KEY}`,
  CLICK_DATA_API: `click${EVENT_KEY}${DATA_API_KEY}`,
  NAVIGATE_DATA_API: `navigate${EVENT_KEY}${DATA_API_KEY}`
}

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class Nav {
  constructor(element, config) {
    this._element = element
    this._config = this._getConfig(config)
    this._confirmElements = this._config.confirm ? this._getConfirmElements() : null
    this._disabled = true
    if (this._config.enable) {
      this.enable()
    }
  }

  // Getters

  static get Event() {
    return Event
  }

  // Public

  toggle() {
    this._disabled = !this._disabled
  }

  enable() {
    this._disabled = false
  }

  disable() {
    this._disabled = true
  }

  trigger(details) {
    if (this._disabled) {
      return
    }

    const { event } = details
    if (event) {
      event.preventDefault()
    }

    this._navigate(details)
  }

  confirm(details) {
    if (this._disabled || !this._confirmElements) {
      return
    }

    const { href, confirmed = false, event } = details
    if (confirmed) {
      return
    }

    if (event) {
      event.preventDefault()
    }

    const accept = () => this._navigate({ href, confirmed: true })
    Confirm.chain({ accept, elements: this._confirmElements })
  }

  // Private

  _getConfig(config) {
    config = {
      ...Default,
      ...config
    }
    Util.typeCheckConfig(NAME, config, DefaultType)
    return config
  }

  _getConfirmElements() {
    let elems

    if (Util.isElement(this._config.confirm)) {
      elems = [this._config.confirm[0] || this._config.confirm]
    } else {
      elems = [...document.querySelectorAll(this._config.confirm)]
    }

    return elems.length ? elems : null
  }

  _navigate({ href, confirmed = false, event }) {
    const navEvent = $.Event(Event.NAVIGATE, { confirmed, href, event })

    $(this._element).trigger(navEvent)

    if (navEvent.isDefaultPrevented()) {
      return
    }

    if (href) {
      window.location = href
    }
  }

  // Static

  static _jQueryInterface(config, ...args) {
    return this.each(function () {
      let data = $(this).data(DATA_KEY)
      const _config = {
        ...Default,
        ...$(this).data(),
        ...typeof config === 'object' && config ? config : {}
      }

      if (!data) {
        data = new Nav(this, _config)
        $(this).data(DATA_KEY, data)
      }

      if (typeof config === 'string') {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config](...args)
      }
    })
  }
}

/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */

$(Selector.DATA_NAV_CONFIRM).on(Event.CLICK_DATA_API, Selector.DATA_TOGGLE_NAV, function () {
  const selector = Util.getSelectorFromElement(this)
  Nav._jQueryInterface.call($(selector), 'toggle')
})

$(Selector.DATA_NAV_CONFIRM).on(Event.CLICK_DATA_API, Selector.DATA_NAV_TRIGGER, function (event) {
  if (event.isDefaultPrevented() || $(this).closest(Selector.DATA_NAV_IGNORE).length > 0) {
    return
  }

  Nav._jQueryInterface.call($(this), 'trigger', { href: this.href, event })
})

$(Selector.DATA_NAV_CONFIRM).on(Event.NAVIGATE_DATA_API, function (event) {
  const { href, confirmed = false } = event
  Nav._jQueryInterface.call($(this), 'confirm', { href, confirmed, event })
})

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
 */

$.fn[NAME] = Nav._jQueryInterface
$.fn[NAME].Constructor = Nav
$.fn[NAME].noConflict = () => {
  $.fn[NAME] = JQUERY_NO_CONFLICT
  return Nav._jQueryInterface
}

export default Nav
